import React from "react";
import { createRoot } from "react-dom/client";
import "assets/css/App.css";
import { HashRouter, Route, Routes, Navigate } from "react-router-dom";
import AuthLayout from "layouts/auth";
import AdminLayout from "layouts/admin";
import RtlLayout from "layouts/rtl";
import { ChakraProvider, ToastProvider } from "components-lib";
import theme from "theme/theme";
import { AuthProvider } from "contexts/AuthContext";
import UnifiedLogin from "views/UnifiedLogin";
import ProtectedRoute from "components/ProtectedRoute";
import RoleBasedRedirect from "components/RoleBasedRedirect";
import IdleLogoutOverlay from "components/IdleLogoutOverlay";
import GoogleAuthCallback from "views/oauth/GoogleAuthCallback";
import terms from "assets/terms_privacy_policy/Kifayti health Doctors Terms and Conditions.pdf"
import privacy from "assets/terms_privacy_policy/Kifayti health PRIVACY POLICY.pdf"
import { ChatProvider } from "contexts/ChatContext";

// The GoogleAuthCallback implementation has been moved to the dedicated component
// at src/views/oauth/GoogleAuthCallback.js. This index bootstrap now imports and
// uses that component for direct-path callback handling and for the hash-route.

/**
 * If Google redirected to the direct pathname `/google-auth-callback?code=...`
 * then HashRouter won't match because HashRouter uses the hash. Detect that case
 * and render the callback UI directly (so the code can be exchanged immediately).
 *
 * Otherwise, render the existing app (HashRouter based).
 */
const isDirectGoogleCallback =
  window.location.pathname === "/google-auth-callback";
const isTermsPage = window.location.pathname === "/terms";
const isPrivacyPage = window.location.pathname === "/privacy";

// Get the root container
const container = document.getElementById("root");
const root = createRoot(container);

// Determine what to render based on the current path
let appContent;

if (isDirectGoogleCallback) {
  // Render only the callback component (this covers the direct-redirect case)
  appContent = (
    <ChakraProvider theme={theme}>
      <ToastProvider>
        <React.StrictMode>
          <GoogleAuthCallback />
        </React.StrictMode>
      </ToastProvider>
    </ChakraProvider>
  );
} else
  if (isTermsPage) {
  // Render a simple Terms and Conditions page for the /terms route
  appContent = (
    <ChakraProvider theme={theme}>
      <ToastProvider>
        <React.StrictMode>
          <iframe src={terms} title="Terms and Conditions" style={{ width: "100vw", height: "100vh", border: "none" }}></iframe>
        </React.StrictMode>
      </ToastProvider>
    </ChakraProvider>
  );
} else if (isPrivacyPage) {
  // Render a simple Privacy Policy page for the /privacy route
  appContent = (
    <ChakraProvider theme={theme}>
      <ToastProvider>
        <React.StrictMode>
          <iframe src={privacy} title="Privacy Policy" style={{ width: "100vw", height: "100vh", border: "none" }}></iframe>
        </React.StrictMode>
      </ToastProvider>
    </ChakraProvider>
  );
} else {
  // Default app rendering (unchanged)
  appContent = (
    <ChakraProvider theme={theme}>
      <ToastProvider>
        <React.StrictMode>
          <HashRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ChatProvider>
              <AuthProvider>
                <IdleLogoutOverlay />
                <Routes>
                  <Route path="/login" element={<UnifiedLogin />} />
                  <Route path="/google-auth-callback" element={<GoogleAuthCallback />} />
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<RoleBasedRedirect />} />
                </Routes>
              </AuthProvider>
            </ChatProvider>
          </HashRouter>
        </React.StrictMode>
      </ToastProvider>
    </ChakraProvider>
  );
}

// Render the app with the single root instance
root.render(appContent);
